import { setAccessToken } from '@/lib/api/client';
import { getMeApi, logoutApi, refreshAccessTokenApi } from '@/lib/auth/authApi';
import type { AuthUser } from '@/lib/auth/types';
import type { Role } from '@shared/enums/role.enum';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

interface PendingChallenge {
  token: string;
  type: string;
  methodsAllowed: string[];
  obfuscatedEmail: string;
}

interface AuthState {
  user: AuthUser | null;
  accessToken: string | null;
  isAuthenticated: boolean;
  /** Challenge MFA en cours — jamais persisté */
  pendingChallenge: PendingChallenge | null;

  login: (user: AuthUser, accessToken: string) => void;
  logout: () => void;
  hasRole: (roles: Role | Role[]) => boolean;
  updateUser: (user: Partial<AuthUser>) => void;
  refreshSession: () => Promise<void>;
  tryRestoreSession: () => Promise<void>;
  setChallenge: (data: PendingChallenge) => void;
  clearChallenge: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      accessToken: null,
      isAuthenticated: false,
      pendingChallenge: null,

      login: (user, accessToken) => {
        setAccessToken(accessToken);
        set({ user, accessToken, isAuthenticated: true, pendingChallenge: null });
      },

      logout: () => {
        // Invalider le refresh token côté serveur pour couper le SSO cross-app
        logoutApi().catch(() => {});
        setAccessToken(null);
        set({ user: null, accessToken: null, isAuthenticated: false, pendingChallenge: null });
      },

      hasRole: roles => {
        const { user } = get();
        if (!user) return false;
        const allowed = Array.isArray(roles) ? roles : [roles];
        return allowed.includes(user.role);
      },

      updateUser: partial => {
        const { user } = get();
        if (user) {
          set({ user: { ...user, ...partial } });
        }
      },

      refreshSession: async () => {
        if (!get().isAuthenticated) return;
        try {
          const user = await getMeApi();
          set({ user });
        } catch {
          get().logout();
        }
      },

      // Tente de restaurer la session depuis le cookie refresh token
      // (utile quand l'utilisateur s'est connecté depuis le front public)
      tryRestoreSession: async () => {
        try {
          const { accessToken: newToken } = await refreshAccessTokenApi();
          setAccessToken(newToken);
          const user = await getMeApi();
          get().login(user, newToken);
        } catch {
          // Aucun cookie valide — rester déconnecté silencieusement
        }
      },

      setChallenge: data => {
        set({ pendingChallenge: data });
      },

      clearChallenge: () => {
        set({ pendingChallenge: null });
      },
    }),
    {
      name: 'bc_admin_auth',
      partialize: state => ({
        user: state.user,
        accessToken: state.accessToken,
        isAuthenticated: state.isAuthenticated,
        // pendingChallenge intentionnellement exclu de la persistance
      }),
      onRehydrateStorage: () => state => {
        if (state?.accessToken) {
          setAccessToken(state.accessToken);
        }
      },
    },
  ),
);

// Initialisation synchrone du token AVANT le premier rendu React.
// onRehydrateStorage est appelé en microtask (async) : entre le moment où
// Zustand réhydrate `isAuthenticated: true` depuis localStorage et le moment
// où setAccessToken() est appelé, React Query voit isAuthenticated=true et
// émet les requêtes SANS token → 401. Ce bloc lit le token directement et le
// définit synchronement pour éviter la race condition.
if (typeof window !== 'undefined') {
  try {
    const raw = localStorage.getItem('bc_admin_auth');
    if (raw) {
      const parsed = JSON.parse(raw) as { state?: { accessToken?: string | null } };
      if (parsed.state?.accessToken) {
        setAccessToken(parsed.state.accessToken);
      }
    }
  } catch {
    // JSON invalide ou localStorage indisponible — pas bloquant
  }
}

// Écouter les déconnexions forcées (refresh token expiré)
if (typeof window !== 'undefined') {
  window.addEventListener('auth:logout', () => {
    useAuthStore.getState().logout();
  });
}
